import type { UIMessage } from 'ai'
import { Box } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LlmChatMessageDto } from '@/shared/hooks/api/llmChat'
import {
  isDismissedCatalogRecommendationResolution,
  parseProductUpdateEvent,
} from '@/shared/hooks/api/llmChatProductUpdates'
import {
  ProductUpdateEvent,
  ProductUpdateEventKey,
} from '@/shared/hooks/api/llmChatProductUpdates'

type LlmChatSystemEventBubbleProps = {
  message: UIMessage
  persistedMessage?: LlmChatMessageDto
}

function getMessageTextFallback(message: UIMessage): string {
  const text = message.parts
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('')
    .trim()
  return text
}

function getProductSystemEventText(event: ProductUpdateEvent): string {
  const { t } = useTranslation(['llmChat', 'productCatalog'])

  if (event.eventKey === ProductUpdateEventKey.PRODUCT_METADATA_UPDATED) {
    const fieldsText = event.params.updatedFields
      .map((field) => t(`detail.fields.${field}`, { ns: 'productCatalog' }))
      .join(t('productUpdateEvents.fieldSeparator'))
    return t(
      `productUpdateEvents.${ProductUpdateEventKey.PRODUCT_METADATA_UPDATED}`,
      {
        fields: fieldsText,
      }
    )
  }
  if (event.eventKey === ProductUpdateEventKey.PRODUCT_SOURCE_ADDED) {
    return t(
      `productUpdateEvents.${ProductUpdateEventKey.PRODUCT_SOURCE_ADDED}`,
      {
        sourceName: event.params.sourceName,
      }
    )
  }
  if (event.eventKey === ProductUpdateEventKey.PRODUCT_SOURCE_DELETED) {
    return t(
      `productUpdateEvents.${ProductUpdateEventKey.PRODUCT_SOURCE_DELETED}`,
      {
        sourceName: event.params.sourceName,
      }
    )
  }
  if (event.eventKey === ProductUpdateEventKey.PRODUCT_MANUFACTURER_ASSIGNED) {
    return t(
      `productUpdateEvents.${ProductUpdateEventKey.PRODUCT_MANUFACTURER_ASSIGNED}`,
      {
        manufacturerName: event.params.manufacturerName,
      }
    )
  }
  if (
    event.eventKey === ProductUpdateEventKey.PRODUCT_MANUFACTURER_UNASSIGNED
  ) {
    return t(
      `productUpdateEvents.${ProductUpdateEventKey.PRODUCT_MANUFACTURER_UNASSIGNED}`,
      {
        manufacturerName: event.params.manufacturerName,
      }
    )
  }
  return ''
}

function formatCreatedAt(
  createdAt: string | undefined,
  locale: string
): string | null {
  if (!createdAt) {
    return null
  }
  const date = new Date(createdAt)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

export function LlmChatSystemEventBubble({
  message,
  persistedMessage,
}: LlmChatSystemEventBubbleProps) {
  const { i18n } = useTranslation(['llmChat', 'productCatalog'])

  if (
    isDismissedCatalogRecommendationResolution(persistedMessage?.contentJson)
  ) {
    return null
  }

  const event = parseProductUpdateEvent(persistedMessage?.contentJson)
  const content =
    event !== null
      ? getProductSystemEventText(event)
      : getMessageTextFallback(message)
  const createdAtLabel = formatCreatedAt(
    persistedMessage?.createdAt,
    i18n.language
  )

  if (content.length === 0) {
    return null
  }

  return (
    <div className='w-full'>
      <div className='w-full rounded-md border border-border/70 bg-accent/25 px-3 py-1'>
        <div className='flex items-start gap-2'>
          <Box
            className='mt-0.5 size-4 shrink-0 text-muted-foreground'
            aria-hidden
          />
          <div className='min-w-0 flex-1'>
            <p className='text-sm text-foreground/90'>{content}</p>
            {createdAtLabel ? (
              <p className='text-xs text-muted-foreground'>{createdAtLabel}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
